"use client";

import { createContext, useContext, useEffect } from "react";
import { useDndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import { useCombinedRefs, useEvent } from "@dnd-kit/utilities";

import { t } from "@/lib/localization";
import { LomdaV3Block } from "@/lib/utils/lomdaV3/types";
import { blockLabelKey } from "../lib/sectionRegistry";
import { BlockList } from "../lib/types";
import { DragData, DropTarget, readDropTarget } from "../lib/dropTarget";

/** How many container slots wrap this spot; 0 = the page itself. */
export const SlotDepthContext = createContext(0);

/** Hover a closed tab / step header this long while dragging to open it. */
const OPEN_ON_HOVER_MS = 500;

/**
 * Any block, top-level or nested: draggable by its grip only, and a drop
 * target for "before / after me". Both register on the block's own node.
 */
export function useBlockDnd(
    block: LomdaV3Block,
    list: BlockList,
    index: number,
    disabled = false
) {
    const depth = useContext(SlotDepthContext);
    const data: DragData = {
        kind: "block",
        label: t(blockLabelKey(block)),
        type: block.type,
        list,
        index,
        depth,
    };
    const drag = useDraggable({ id: block.id, data, disabled });
    const drop = useDroppable({ id: block.id, data, disabled });

    return {
        setNodeRef: useCombinedRefs(drag.setNodeRef, drop.setNodeRef),
        handle: { attributes: drag.attributes, listeners: drag.listeners },
        isDragging: drag.isDragging,
    };
}

/** A tab / step header: dropping on it appends to that slot, and hovering it
 *  opens the slot so the block can be placed exactly. */
export function useSlotHeaderDrop({
    containerId,
    slotId,
    count,
    open,
    onOpen,
}: {
    containerId: string;
    slotId: string;
    count: number;
    open: boolean;
    onOpen: () => void;
}) {
    const depth = useContext(SlotDepthContext) + 1;
    const data: DragData = { kind: "slot-header", containerId, slotId, count, depth };
    const { setNodeRef, isOver } = useDroppable({
        id: `slot-header:${containerId}:${slotId}`,
        data,
    });
    const openSlot = useEvent(onOpen);

    useEffect(() => {
        if (!isOver || open) return;
        const timer = setTimeout(openSlot, OPEN_ON_HOVER_MS);
        return () => clearTimeout(timer);
    }, [isOver, open, openSlot]);

    return { setNodeRef, isOver };
}

/** The current drop target while dragging. Call it only in small leaf
 *  components: it re-renders on every pointer move. */
export function useDropTarget(): DropTarget | null {
    return readDropTarget(useDndContext().collisions);
}
